import React, { useState } from 'react';
import { Server, X, Shield, AlertTriangle, CheckCircle, Power, Trash2 } from 'lucide-react';
import { tradingApi, GatewayStatus } from '../services/api';
import GatewaySwitchWarningModal from './GatewaySwitchWarningModal';
import ModalPortal from './ModalPortal';
import { useAuth } from '../context/AuthContext';

interface GatewayConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void | Promise<void>;
    currentUsername?: string;
    currentMode?: 'paper' | 'live';
    savedGateways?: GatewayStatus[];
}

const GatewayConfigModal: React.FC<GatewayConfigModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    currentUsername,
    currentMode,
    savedGateways,
}) => {
    const { user } = useAuth();
    const isInvestor = user?.role === 'investor';

    const [username, setUsername] = useState(currentUsername || '');
    const [password, setPassword] = useState('');
    const [mode, setMode] = useState<'paper' | 'live'>(isInvestor ? 'live' : (currentMode || 'paper'));
    // Default gatewayId to the mode name to ensure separation by default
    const [gatewayId, setGatewayId] = useState<string>(isInvestor ? 'live' : (currentMode || 'paper'));
    const [configStatus, setConfigStatus] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
    const [loading, setLoading] = useState(false);

    // Warning Modal State
    const [showWarning, setShowWarning] = useState(false);
    const [activating, setActivating] = useState(false);

    // Helper to find saved config for a mode
    const findSavedConfig = (targetMode: string) => {
        if (!savedGateways) return null;
        // First try to find by ID matching mode (e.g. id="paper")
        let found = savedGateways.find(g => g.id === targetMode);
        // Fallback to finding by mode
        if (!found) {
            found = savedGateways.find(g => g.mode === targetMode);
        }
        return found;
    };

    const savedConfig = findSavedConfig(mode);
    const isActive = savedConfig?.is_active;

    // Reset state when modal opens
    React.useEffect(() => {
        if (isOpen) {
            const initialMode = isInvestor ? 'live' : (currentMode || 'paper');
            setMode(initialMode);
            setGatewayId(initialMode); // Default ID to mode name

            // Try to pre-fill from saved gateways first
            const saved = findSavedConfig(initialMode);
            if (saved && saved.username) {
                setUsername(saved.username);
                if (saved.id) {
                    setGatewayId(saved.id);
                }
            } else {
                setUsername(currentUsername || '');
            }

            setPassword('');
            setConfigStatus(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isInvestor]);

    if (!isOpen) return null;

    const handleModeSwitch = (newMode: 'paper' | 'live') => {
        setMode(newMode);
        setConfigStatus(null);

        // Try to find saved config for the new mode
        const saved = findSavedConfig(newMode);

        if (saved && saved.username) {
            setUsername(saved.username);
            setGatewayId(saved.id);
        } else {
            // If switching back to initial mode, restore initial props
            if (newMode === currentMode) {
                setUsername(currentUsername || '');
            } else {
                setUsername('');
            }
            // Default ID to new mode name if no saved config found
            setGatewayId(newMode);
        }

        setPassword(''); // Always clear password for security
    };

    const handleSaveConfig = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setConfigStatus({ type: 'error', message: 'Username and password are required' });
            return;
        }

        setLoading(true);
        setConfigStatus({ type: 'info', message: 'Saving configuration...' });

        try {
            const response = await tradingApi.configureIBGateway(
                username,
                password,
                mode,
                gatewayId || undefined,
                undefined, // Port is handled automatically by backend
                undefined  // No renaming needed
            );
            if (response.success) {
                setConfigStatus({
                    type: 'success',
                    message: isInvestor
                        ? 'Configuration saved. Preparing broker controls...'
                        : 'Configuration saved. You can now activate this gateway.',
                });
                setPassword('');
                await Promise.resolve(onSuccess());
                if (isInvestor) {
                    setTimeout(onClose, 700);
                }
            } else {
                setConfigStatus({ type: 'error', message: response.error || 'Failed to configure gateway' });
            }
        } catch (err: any) {
            setConfigStatus({ type: 'error', message: err.message || 'An error occurred' });
        } finally {
            setLoading(false);
        }
    };

    const handleActivateClick = () => {
        setShowWarning(true);
    };

    const handleConfirmSwitch = async () => {
        setActivating(true);
        try {
            // Use the gatewayId from state (which should match the saved one if we are in this state)
            // Or better, use the ID from the saved config we found
            const targetId = savedConfig?.id || gatewayId;

            const response = await tradingApi.activateIBGateway(targetId);

            if (response.success) {
                setConfigStatus({ type: 'success', message: 'Gateway activated successfully!' });
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1500);
            } else {
                setConfigStatus({ type: 'error', message: response.error || 'Failed to activate gateway' });
            }
        } catch (err: any) {
            setConfigStatus({ type: 'error', message: err.message || 'Failed to activate gateway' });
        } finally {
            setActivating(false);
            setShowWarning(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!savedConfig || !savedConfig.id) return;
        if (!window.confirm(`Are you sure you want to delete the configuration for ${savedConfig.mode} mode? This will stop the gateway if running.`)) return;

        setLoading(true);
        setConfigStatus({ type: 'info', message: 'Deleting configuration...' });

        try {
            const response = await tradingApi.deleteIBGatewayConfig(savedConfig.id);
            if (response.success) {
                setConfigStatus({ type: 'success', message: 'Configuration deleted.' });
                setUsername('');
                setPassword('');
                // If the deleted one was the current one, reset state to empty
                // Delay close/refresh
                setTimeout(() => {
                    onSuccess();
                    // Don't close immediately so user sees message
                }, 1000);
            } else {
                setConfigStatus({ type: 'error', message: response.error || 'Failed to delete configuration' });
            }
        } catch (err: any) {
            setConfigStatus({ type: 'error', message: err.message || 'An error occurred' });
        } finally {
            setLoading(false);
        }
    };

    return (
      <ModalPortal>
        <>
            <div
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
            >
                <div className="bg-[#121222] border border-white/10 rounded-2xl w-full max-w-lg shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden animate-in fade-in zoom-in duration-200">
                    <div className="p-6 border-b border-white/10 flex items-center justify-between">
                        {/* ... header ... */}
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-neon/10 text-neon">
                                <Server className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Gateway Settings</h3>
                                <p className="text-xs text-muted">
                                    {username ? `Currently configured: ${username}` : 'Configure IB credentials'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-muted hover:text-white transition"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        <form onSubmit={handleSaveConfig} className="space-y-6" autoComplete="off">
                            {/* ... inputs ... */}
                            <div>
                                <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">Trading Mode</label>
                                <div className={`grid ${isInvestor ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                                    {!isInvestor && (
                                        <button
                                            type="button"
                                            onClick={() => handleModeSwitch('paper')}
                                            className={`p-3 rounded-xl border transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium ${mode === 'paper'
                                                ? 'bg-neon/10 border-neon text-neon shadow-[0_0_15px_rgba(34,197,94,0.1)]'
                                                : 'bg-black/20 border-white/5 text-muted hover:border-white/20 hover:bg-white/5'
                                                }`}
                                        >
                                            <Shield className="w-4 h-4" />
                                            Paper
                                            {findSavedConfig('paper')?.is_active && <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px]">ACTIVE</span>}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleModeSwitch('live')}
                                        disabled={isInvestor}
                                        className={`p-3 rounded-xl border transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium ${mode === 'live'
                                            ? 'bg-neon/10 border-neon text-neon shadow-[0_0_15px_rgba(34,197,94,0.1)]'
                                            : 'bg-black/20 border-white/5 text-muted hover:border-white/20 hover:bg-white/5'
                                            } ${isInvestor ? 'opacity-90 cursor-default' : ''}`}
                                    >
                                        <AlertTriangle className="w-4 h-4" />
                                        Live Production
                                        {findSavedConfig('live')?.is_active && <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px]">ACTIVE</span>}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">TWS Username</label>
                                <input
                                    type="text"
                                    name="tws-username"
                                    id="tws-username"
                                    autoComplete="off"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-neon focus:ring-1 focus:ring-neon/50 transition-all duration-200"
                                    placeholder="Enter IB username"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">TWS Password</label>
                                <input
                                    type="password"
                                    name="tws-password"
                                    id="tws-password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-neon focus:ring-1 focus:ring-neon/50 transition-all duration-200"
                                    placeholder="Enter IB password"
                                />
                            </div>

                            {configStatus && (
                                <div className={`p-4 rounded-xl flex items-start gap-3 border ${configStatus.type === 'error' ? 'bg-red-500/5 border-red-500/20 text-red-400' :
                                    configStatus.type === 'success' ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400' :
                                        'bg-blue-500/5 border-blue-500/20 text-blue-400'
                                    }`}>
                                    {configStatus.type === 'error' && <AlertTriangle className="w-5 h-5 shrink-0" />}
                                    {configStatus.type === 'success' && <CheckCircle className="w-5 h-5 shrink-0" />}
                                    {configStatus.type === 'info' && <div className="w-5 h-5 shrink-0 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                                    <p className="text-sm font-medium">{configStatus.message}</p>
                                </div>
                            )}

                            <div className="pt-2 flex flex-col gap-3">
                                <div className="flex items-center gap-2">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 hover:text-blue-200 font-medium py-3.5 rounded-xl transition-all duration-200 border border-blue-500/20 hover:border-blue-500/40 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            'Saving...'
                                        ) : (
                                            <>
                                                <CheckCircle className="w-5 h-5" />
                                                Save
                                            </>
                                        )}
                                    </button>

                                    {savedConfig && !isActive && (
                                        <button
                                            type="button"
                                            onClick={handleActivateClick}
                                            disabled={loading}
                                            className="flex-1 bg-neon hover:bg-neon-hover text-black font-bold py-3.5 rounded-xl transition-all duration-200 shadow-lg shadow-neon/20 hover:shadow-neon/40 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 active:translate-y-0"
                                            title="Activate this gateway"
                                        >
                                            <Power className="w-5 h-5" />
                                            Activate
                                        </button>
                                    )}

                                    {savedConfig && (
                                        <button
                                            type="button"
                                            onClick={handleDeleteConfig}
                                            disabled={loading}
                                            title="Delete configuration"
                                            className="w-14 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-medium py-3.5 rounded-xl transition-all duration-200 border border-red-500/20 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>

                                {isActive && (
                                    <div className="w-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-medium py-3.5 rounded-xl flex items-center justify-center gap-2">
                                        <CheckCircle className="w-5 h-5" />
                                        Currently Active
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <GatewaySwitchWarningModal
                isOpen={showWarning}
                onClose={() => setShowWarning(false)}
                onConfirm={handleConfirmSwitch}
                targetGatewayName={`${mode === 'live' ? 'Live' : 'Paper'} (${username})`}
                isProcessing={activating}
            />
        </>
      </ModalPortal>
    );
};

export default GatewayConfigModal;
